import { useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { loadImpedanceData, selectImpedanceReport } from "../../redux/impedanceSlice";
import { ErrorComponent } from "../Error";
import { Loading } from "../Loading";
import { DateRange } from "../Report/ReportContainer";
import { ImpedanceReport } from "./ImpedanceReport";

export const ImpedanceReportContainer: React.FC = () => {
  const selectedDevice = useAppSelector(state => state.patient.selectedDevice);
  const { loading, impedanceData, error } = useSelector(selectImpedanceReport);
  const dispatch = useAppDispatch();
  const refreshImpedanceData = useCallback(
    async (startDate: Date = null, endDate: Date = null) => {
      dispatch(loadImpedanceData({ startDate, endDate }));
    },
    [dispatch]
  );

  const reportDate = new Date(impedanceData?.RptDt);
  const deviceImpedanceData = impedanceData?.RptImpDevs?.find(
    dev => dev.DevTyp === selectedDevice.deviceType && dev.DevSerial === selectedDevice.serialNumber
  );
  const dateRange = {
    startDate: new Date(deviceImpedanceData?.DtRngStTs),
    endDate: new Date(deviceImpedanceData?.DtRngEndTs),
  };

  const dateRangeLimits = {
    startDate: new Date(deviceImpedanceData?.DtRngMinTs),
    endDate: new Date(deviceImpedanceData?.DtRngMaxTs),
  };

  // Reload the data when the selected device changes
  useEffect(() => {
    refreshImpedanceData();
  }, [refreshImpedanceData, selectedDevice]);

  const handleDateRangeChanged = (newDateRange: DateRange) => {
    if (newDateRange.startDate === null && newDateRange.endDate === null) {
      refreshImpedanceData();
    } else {
      refreshImpedanceData(newDateRange.startDate, newDateRange.endDate);
    }
  };
  const handleRefreshImpedanceData = useCallback(() => {
    refreshImpedanceData(dateRange.startDate, dateRange.endDate);
  }, [dateRange.endDate, dateRange.startDate, refreshImpedanceData]);

  if (!selectedDevice) {
    return <div>Please select a device to view the impedance report.</div>;
  } else if (error) {
    return <ErrorComponent tokenExpired={error.type === "token expired"} message={error.message} />;
  } else if (!deviceImpedanceData) {
    return <Loading />;
  } else {
    return (
      <ImpedanceReport
        loading={loading}
        reportDate={reportDate}
        deviceImpedanceData={deviceImpedanceData}
        refreshReportData={handleRefreshImpedanceData}
        dateRange={dateRange}
        dateRangeLimits={dateRangeLimits}
        onDateRangeChanged={handleDateRangeChanged}
      />
    );
  }
};
